import { useEffect, useMemo, useRef, useState } from 'react'
import IPModal from '../../../../../components/IPModal'
import Table from '../../../../../components/Table'
import {
  buildDrillDailyChart,
  buildDrillHourlyChart,
  buildDrillOutcomeChart,
  buildDrillTiles,
  showsSection,
  toDrillPath,
  visibleOutcomes,
} from '../drilldownModel'
import { IChatManagementDrilldown, IDrillSelection } from '../models'
import { DIMENSION_LABELS, drillSelection, formatLocalDay, successTreatmentLabel } from '../viewModel'
import { DrillBarChart } from './ManagementReportCharts'
import QuestionList, { QuestionsLoader } from './QuestionList'
import { countCell, evaluatedRateCell, makeDrillCell, percentCell } from './ReportParts'
import styles from './ChatbotManagementReport.module.scss'

/** Yolun tamamı gider; sunucu tüm adımların kesişimini döner (ADR-022 §1). */
export type DrilldownLoader = (fromUtc: Date, toUtc: Date, path: IDrillSelection[]) => Promise<IChatManagementDrilldown>

interface DrilldownPanelProps {
  stack: IDrillSelection[]
  fromUtc: Date
  toUtc: Date
  loadDetail: DrilldownLoader
  loadQuestions: QuestionsLoader
  onNavigate: (selection: IDrillSelection) => void
  onBack: () => void
  onClose: () => void
}

type PanelState = 'loading' | 'ready' | 'error' | 'forbidden'

/** ClientProxy HTTP hatalarını IException (httpStatusCode) olarak reddeder; 403 = Detay yetkisi yok. */
const isForbidden = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && (error as { httpStatusCode?: number }).httpStatusCode === 403

/** Yüzen B-Gini sohbet düğmesi (ChatWidget) z-index 99999'dadır; pencere onun üstünde kalmazsa Kapat düğmesini örter. */
const PANEL_LAYER_STYLE = { zIndex: 100000 }
/** components/Table varsayılan sayfa boyu; bu kadar satıra sığan tabloda sayfalama kontrolleri gösterilmez. */
const TABLE_PAGE_SIZE = 10

const tableCardClass = (rowCount: number): string =>
  rowCount <= TABLE_PAGE_SIZE ? `${styles.tableCard} ${styles.singlePageTable}` : styles.tableCard

const DRILL_SCOPE_NOTE = 'Her adım bir öncekinin içinde daraltır; sayılar yolun tamamına aittir.'
const FORBIDDEN_MESSAGE =
  'Detay için yetkiniz yok. Yöneticinizden “Chatbot Raporları – Yönetim Raporu Detayı” yetkisini isteyin.'

export default function DrilldownPanel({
  stack,
  fromUtc,
  toUtc,
  loadDetail,
  loadQuestions,
  onNavigate,
  onBack,
  onClose,
}: DrilldownPanelProps) {
  const pathKey = toDrillPath(stack)
  const stackRef = useRef(stack)
  stackRef.current = stack
  const [state, setState] = useState<PanelState>('loading')
  const [detail, setDetail] = useState<IChatManagementDrilldown | null>(null)
  const sequenceRef = useRef(0)

  useEffect(() => {
    if (pathKey.length === 0) return undefined
    const requestSequence = ++sequenceRef.current
    setState('loading')

    loadDetail(fromUtc, toUtc, stackRef.current)
      .then(response => {
        if (requestSequence !== sequenceRef.current) return
        setDetail(response)
        setState('ready')
      })
      .catch(error => {
        if (requestSequence !== sequenceRef.current) return
        setDetail(null)
        setState(isForbidden(error) ? 'forbidden' : 'error')
      })

    return () => {
      sequenceRef.current += 1
    }
  }, [pathKey, fromUtc, toUtc, loadDetail])

  const outcomeColumns = useMemo(
    () => [
      {
        Header: 'Yanıt Türü',
        accessor: 'code',
        Cell: makeDrillCell(onNavigate, row => drillSelection.outcome(row.code)),
      },
      { Header: 'Anlamı', accessor: 'label' },
      { Header: 'Adet', accessor: 'count', Cell: countCell },
      { Header: 'Pay', accessor: 'share', Cell: percentCell },
      { Header: 'Başarı Sayılır?', accessor: 'treatment' },
    ],
    [onNavigate]
  )
  const intentColumns = useMemo(
    () => [
      {
        Header: 'İşlev',
        accessor: 'label',
        Cell: makeDrillCell(onNavigate, row => drillSelection.intent(row.intent, row.label)),
      },
      { Header: 'Soru', accessor: 'total', Cell: countCell },
      { Header: 'Başarı %', accessor: 'successRate', Cell: evaluatedRateCell },
      { Header: 'Veri Yok %', accessor: 'noResultRate', Cell: evaluatedRateCell },
      { Header: 'Başarısız', accessor: 'failed', Cell: countCell },
    ],
    [onNavigate]
  )
  const channelColumns = useMemo(
    () => [
      {
        Header: 'Kanal',
        accessor: 'label',
        Cell: makeDrillCell(onNavigate, row => drillSelection.channel(row.key, row.label)),
      },
      { Header: 'Soru', accessor: 'total', Cell: countCell },
      { Header: 'Pay', accessor: 'share', Cell: percentCell },
      { Header: 'Başarı %', accessor: 'successRate', Cell: evaluatedRateCell },
      { Header: 'Veri Yok %', accessor: 'noResultRate', Cell: evaluatedRateCell },
    ],
    [onNavigate]
  )
  const dayColumns = useMemo(
    () => [
      {
        Header: 'Gün',
        accessor: 'date',
        Cell: makeDrillCell(
          onNavigate,
          row => drillSelection.date(row.date),
          value => formatLocalDay(value)
        ),
      },
      { Header: 'Soru', accessor: 'total', Cell: countCell },
      { Header: 'Başarı %', accessor: 'successRate', Cell: evaluatedRateCell },
      { Header: 'Veri Yok %', accessor: 'noResultRate', Cell: evaluatedRateCell },
    ],
    [onNavigate]
  )

  const rows = useMemo(() => {
    if (!detail) return null
    return {
      outcomes: visibleOutcomes(detail).map(row => ({
        code: row.Code,
        label: row.Label,
        count: row.Count,
        share: row.Share,
        treatment: successTreatmentLabel(row.SuccessTreatment),
      })),
      intents: detail.IntentRows.map(row => ({
        intent: row.Intent,
        label: row.Label,
        total: row.Total,
        successRate: row.SuccessRate,
        noResultRate: row.NoResultRate,
        failed: row.Failed,
        noRate: row.Total === 0,
      })),
      channels: detail.Channels.filter(row => row.Total > 0).map(row => ({
        key: row.Key,
        label: row.Label,
        total: row.Total,
        share: row.Share,
        successRate: row.SuccessRate,
        noResultRate: row.NoResultRate,
        noRate: row.Total === 0,
      })),
      days: detail.Daily.filter(day => day.Total > 0).map(day => ({
        date: day.Date,
        total: day.Total,
        successRate: day.SuccessRate,
        noResultRate: day.NoResultRate,
        noRate: day.Total === 0,
      })),
    }
  }, [detail])

  // IPModal ancak show false → true geçişinde (CSSTransition "enter-done") görünür ve tıklanır olur; bu yüzden
  // pencere hep yerinde durur ve görünürlüğü show yönetir. Kapanırken son seçim 300 ms'lik solma boyunca çizilir.
  const lastShownRef = useRef<IDrillSelection[]>([])
  if (stack.length > 0) lastShownRef.current = stack
  const shownStack = lastShownRef.current
  const shown = shownStack.length > 0 ? shownStack[shownStack.length - 1] : null
  const shownDimensions = shownStack.map(step => step.dimension)

  const title = !shown
    ? ''
    : state === 'ready' && detail
    ? detail.Title
    : `${DIMENSION_LABELS[shown.dimension]}: ${shown.label}`
  const ready = state === 'ready' && detail !== null && rows !== null

  return (
    <IPModal
      show={stack.length > 0}
      title={title}
      onClose={onClose}
      hideConfirm
      closeOnEscapeKey
      width={1100}
      bodyOverflow
      style={PANEL_LAYER_STYLE}
    >
      {shown ? (
        <div className={styles.drillBody} data-testid='drilldown-panel'>
          <div className={styles.drillToolbar}>
            <nav className={styles.breadcrumb} aria-label='Detay yolu'>
              <span>Rapor</span>
              {shownStack.map((item, index) => (
                <span key={`${item.dimension}-${item.key}-${index}`}>
                  ›{' '}
                  {index === shownStack.length - 1 ? (
                    <strong>
                      {DIMENSION_LABELS[item.dimension]}: {item.label}
                    </strong>
                  ) : (
                    `${DIMENSION_LABELS[item.dimension]}: ${item.label}`
                  )}
                </span>
              ))}
            </nav>
            {shownStack.length > 1 ? (
              <button type='button' className={styles.secondaryButton} onClick={onBack}>
                ‹ Geri
              </button>
            ) : null}
          </div>
          <p className={styles.drillHint}>{DRILL_SCOPE_NOTE}</p>

          {state === 'loading' ? <p role='status'>Detay hazırlanıyor.</p> : null}
          {state === 'error' ? (
            <p role='alert' className={styles.rangeError}>
              Detay yüklenemedi. Lütfen tekrar deneyin.
            </p>
          ) : null}
          {state === 'forbidden' ? (
            <p role='alert' className={styles.rangeError}>
              {FORBIDDEN_MESSAGE}
            </p>
          ) : null}
          {ready && detail!.Summary.TotalTurns === 0 ? <p>Bu seçimde soru yok.</p> : null}

          {ready && detail!.Summary.TotalTurns > 0 ? (
            <>
              <div className={styles.drillTiles}>
                {buildDrillTiles(detail!).map(tile => (
                  <div className={styles.drillTile} key={tile.label}>
                    <span>{tile.label}</span>
                    <strong>{tile.value}</strong>
                  </div>
                ))}
              </div>

              <div className={styles.sectionGrid}>
                {showsSection(shownDimensions, 'daily') ? (
                  <div className={styles.chartCard}>
                    <p className={styles.chartTitle}>Günlük soru</p>
                    <DrillBarChart
                      data={buildDrillDailyChart(detail!)}
                      label='Seçimin günlük soru dağılımı'
                      onSelect={index =>
                        detail!.Daily[index] && onNavigate(drillSelection.date(detail!.Daily[index].Date))
                      }
                    />
                  </div>
                ) : null}
                {showsSection(shownDimensions, 'hourly') ? (
                  <div className={styles.chartCard}>
                    <p className={styles.chartTitle}>Saatlik soru</p>
                    <DrillBarChart
                      data={buildDrillHourlyChart(detail!)}
                      label='Seçimin saatlik soru dağılımı'
                      onSelect={index =>
                        detail!.Hourly[index] && onNavigate(drillSelection.hour(detail!.Hourly[index].Hour))
                      }
                    />
                  </div>
                ) : null}
              </div>

              {showsSection(shownDimensions, 'outcomes') ? (
                <div className={styles.sectionGrid}>
                  <div className={styles.chartCard}>
                    <p className={styles.chartTitle}>Yanıt türleri</p>
                    <DrillBarChart
                      horizontal
                      data={buildDrillOutcomeChart(detail!)}
                      label='Seçimin yanıt türü dağılımı'
                      onSelect={index => {
                        const row = visibleOutcomes(detail!)[index]
                        if (row) onNavigate(drillSelection.outcome(row.Code))
                      }}
                    />
                  </div>
                  <div className={tableCardClass(rows!.outcomes.length)}>
                    <Table columns={outcomeColumns} data={rows!.outcomes} />
                  </div>
                </div>
              ) : null}

              {showsSection(shownDimensions, 'intents') ? (
                <div className={tableCardClass(rows!.intents.length)}>
                  <p className={styles.chartTitle}>Araçlar</p>
                  <Table columns={intentColumns} data={rows!.intents} />
                </div>
              ) : null}

              {showsSection(shownDimensions, 'channels') ? (
                <div className={tableCardClass(rows!.channels.length)}>
                  <p className={styles.chartTitle}>Kanallar</p>
                  <Table columns={channelColumns} data={rows!.channels} />
                </div>
              ) : null}

              {showsSection(shownDimensions, 'daily') ? (
                <div className={tableCardClass(rows!.days.length)}>
                  <p className={styles.chartTitle}>Günler</p>
                  <Table columns={dayColumns} data={rows!.days} />
                </div>
              ) : null}
            </>
          ) : null}

          {state !== 'forbidden' ? (
            <QuestionList path={shownStack} fromUtc={fromUtc} toUtc={toUtc} loadQuestions={loadQuestions} />
          ) : null}
        </div>
      ) : (
        <div />
      )}
    </IPModal>
  )
}
